'use client'

import { SlidePagination } from '@component/card/listings/coverSlide/slide'
import FavouriteButton from '@component/card/listings/favourite'
import TrackButton from '@component/card/listings/track'
import ImageBgLoading from '@component/imageBgLoading'
import useGallery from '@hooks/useGallery'
import useVideoPlayer from '@hooks/useVideoPlayer'
import IconCover from '@icon/home/type-buy.svg'
import IconFloorplan from '@icon/house/floorplan.svg'
import IconVideo from '@icon/house/video-play.svg'
import IconVirtual from '@icon/house/virtual.svg'
import { propertyDataLayer } from '@plugin/google/gtm/modules/property'
import { global } from '@store/index'
import { joinStr } from '@utils'
import { isShowFavoriteButton, isShowTrackButton } from '@utils/house'
import classNames from 'classnames'
import { get, isFunction, map, size } from 'lodash-es'
import { useCallback, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useHouseCoverContext } from '../context'

export default function ImageCover({
  data
}) {

  const {
    cover = '',
    address,
    hasOtherImage,
    showImageBar,
    images,
    floorPlanImages,
    imageTagTidy,
    soldLabel,
    comingSoonLabel,
    isEstimate,
    imageSize
  } = useHouseCoverContext()

  const [index, setIndex] = useState(0)
  const { showGallery } = useGallery()
  // 图片是否可以点击
  // const clickable = useMemo(() => isSaleData(data) || isRentData(data), [data])
  const clickable = showImageBar && !isEstimate && cover && imageSize > 0
  // 主图点击事件
  const clickHandle = useCallback(event => {
    if (!clickable) return

    propertyDataLayer.imageClick()
    showGallery({ house: data, images, floorPlanImages, imageTagTidy, address })(event)
  }, [clickable, data, showGallery, images, floorPlanImages, imageTagTidy, address])

  return (
    <>
      <div className={classNames('grid grid-cols-1 gap-4 md:h-[30vmin] xl:h-[50vmin] md:max-h-500 lg:min-h-[300px]', hasOtherImage ? 'md:grid-cols-[62.5%,1fr]' : '')}>
        <div
          className={classNames('aspect-video min-h-0 relative md:aspect-auto', { 'cursor-pointer overflow-hidden': clickable })}
        >
          {
            cover
              ? (
                <>
                  <ImageBgLoading
                    src={cover}
                    width={900}
                    height={400}
                    alt={`${address}_0`}
                    className={classNames('w-full h-full aspect-auto object-cover', { 'hidden md:block hover:scale-110 transition-transform duration-500': clickable })}
                    priority
                    fetchPriority='high'
                    onClick={clickHandle}
                  ></ImageBgLoading>
                  {
                    clickable && (
                      <Swiper
                        modules={[Navigation]}
                        spaceBetween={4}
                        className='h-full md:!hidden'
                        navigation={{
                          nextEl: '.listings-cover-next',
                          prevEl: '.listings-cover-prev'
                        }}
                        onSlideChange={sw => setIndex(sw.realIndex)}
                        loop
                      >
                        {
                          map(images, (img, idx) => (
                            <SwiperSlide key={idx}>
                              <ImageBgLoading
                                src={img}
                                width={375}
                                height={210}
                                alt={`${address}_${idx}`}
                                className='w-full h-full aspect-auto object-cover block'
                                fetchPriority='high'
                                onClick={showGallery({ house: data, images, floorPlanImages, imageTagTidy, address, index: idx })}
                              ></ImageBgLoading>
                            </SwiperSlide>
                          ))
                        }
                        <SlidePagination index={index} length={size(images)} />
                      </Swiper>
                    )
                  }
                </>
              )
              : (
                <div className='bg-neutral-1 h-full flex flex-col items-center justify-center text-center gap-y-20 py-80 md:py-0'>
                  <IconCover width={136} height={136} className='w-88 h-88 lg:w-136 lg:h-136'></IconCover>
                  <div className='text-xl text-black'>This listing has no images</div>
                </div>
              )
          }
          <div
            className={
              classNames(
                'absolute z-10 inset-x-0 inset-y-0 pointer-events-none',
                { 'bg-black bg-opacity-40': !data.houseId },
                { 'bg-image-mask-top': soldLabel || (cover && comingSoonLabel) }
              )
            }>
            {/* 主图遮罩层 */}
            <div className={classNames(
              'absolute inset-y-0 py-10 flex flex-col justify-between',
              hasOtherImage
                ? 'right-0 left-[calc((100vw_-_min(var(--section-width),_100vw))/2)] pl-[var(--container-pd)] pr-10 lg:pl-0'
                : 'section-wrap left-1/2 -translate-x-1/2'
            )}>
              <div className='flex justify-between items-center'>
                <div>
                  {
                    soldLabel && (
                      <CoverLabelItem label='Recently sold'>
                        {soldLabel}
                      </CoverLabelItem>
                    )
                  }
                  {
                    comingSoonLabel && (
                      <CoverLabelItem label='Coming Soon' textClass={(!cover && comingSoonLabel) ? 'text-black' : undefined}>
                        {comingSoonLabel}
                      </CoverLabelItem>
                    )
                  }
                </div>
                {
                  // 是否显示favorite button
                  isShowFavoriteButton(data) && (
                    <FavouriteButton data={data} signupSource='Save_Property' id='save_listing_cover_img'></FavouriteButton>
                  )
                }
                {
                  // 是否显示track button
                  isShowTrackButton(data) && (
                    <TrackButton data={data} signupSource='Track_Home' className='rounded-4xl'></TrackButton>
                  )
                }
              </div>
              {
                !data.houseId && (
                  <address
                    className={classNames('text-white not-italic text-xl sm:text-3xl font-bold', { 'sm:pb-30': showImageBar })}
                  >
                    {joinStr([get(data, 'street', ''), get(data, 'suburb', ''), get(data, 'district', '')], ', ')}
                  </address>
                )
              }
            </div>
          </div>
        </div>
        {
          hasOtherImage && <OtherImages data={data} clickable={clickable}></OtherImages>
        }
      </div>
    </>
  )
}

export function OtherImages({
  data,
  clickable
}) {

  const { isMobile } = useRecoilValue(global)
  const { address, hasOtherImage, otherImages, video, floorPlanImages, imageTagTidy, threeD, imageSize, images } = useHouseCoverContext()
  const { showGallery } = useGallery()
  const { showVideoPlayer } = useVideoPlayer()

  const clickHandle = useCallback(index => {

    let isFloorPlan = false
    if (index === 1 && size(floorPlanImages)) {
      isFloorPlan = true
      index = size(images)
    } else if (index === 2 && !isMobile && video) {
      return event => {
        propertyDataLayer.videoClick()
        showVideoPlayer({ url: video })(event)
      }
    } else if (index === 3 && threeD) {
      return event => {
        propertyDataLayer.threedClick()
        showVideoPlayer({ url: threeD })(event)
      }
    }
    return event => {
      showGallery({ house: data, images, floorPlanImages, imageTagTidy, address, index })(event)
      if (isFloorPlan) {
        propertyDataLayer.floorplanClick()
      } else {
        propertyDataLayer.imageClick()
      }
    }
  }, [showGallery, showVideoPlayer, data, images, floorPlanImages, imageTagTidy, video, threeD, address, isMobile])

  return hasOtherImage && (
    <div className='grid grid-cols-2 md:grid-rows-[1fr,1fr] gap-4 min-h-0'>
      {
        map(otherImages, (src, idx) => (
          <OtherImageItem
            key={idx}
            src={src}
            idx={idx}
            address={address}
            video={video}
            floorPlanImages={floorPlanImages}
            threeD={threeD}
            imageSize={imageSize}
            onClick={clickHandle(idx + 1)}
            clickable={clickable}
          ></OtherImageItem>
        ))
      }
    </div>
  )
}

function OtherImageItem({
  src,
  address,
  floorPlanImages,
  video,
  threeD,
  imageSize,
  idx,
  onClick,
  clickable
}) {

  const { isEstimate } = useHouseCoverContext()

  const clickHandle = useCallback(event => {
    isFunction(onClick) && onClick(event)
  }, [onClick])
  return (
    <>
      <div
        className={classNames('aspect-video w-full h-full md:aspect-auto min-h-0 relative', { 'cursor-pointer overflow-hidden': clickable }, { 'hidden md:block': [2, 3].includes(idx) })}
        onClick={clickable ? clickHandle : undefined}
      >
        <ImageBgLoading src={src} width={280} height={200} alt={`${address}_${idx + 1}`} className={classNames('block w-full h-full aspect-auto object-cover', { 'hover:scale-110 transition-transform duration-500': clickable })} priority fetchPriority='high'></ImageBgLoading>
        <div className='absolute inset-x-0 inset-y-0 bg-black bg-opacity-50 flex pointer-events-none'>
          <div className='text-white m-auto'>
            {
              idx === 0 && size(floorPlanImages) > 0 && (
                <TagItem text='Floorplan'>
                  <IconFloorplan fill='white' className='w-16 h-16 sm:w-24 sm:h-24'></IconFloorplan>
                </TagItem>
              )
            }
            {
              idx === 1 && video && (
                <TagItem text='Video tour' className='hidden md:block'>
                  <IconVideo fill='white' className='w-16 h-16 sm:w-24 sm:h-24'></IconVideo>
                </TagItem>
              )
            }
            {
              idx === 2 && threeD && (
                <TagItem text='3D Virtual tour'>
                  <IconVirtual fill='white' className='w-16 h-16 sm:w-24 sm:h-24'></IconVirtual>
                </TagItem>
              )
            }
            {
              !isEstimate && idx === 3 && clickable && (
                <TagItem text={`+${imageSize}`} big></TagItem>
              )
            }
            {
              !isEstimate && idx === 1 && clickable && (
                <TagItem text={`+${imageSize}`} big className='md:hidden'></TagItem>
              )
            }
          </div>
        </div>
      </div>
    </>

  )
}

function TagItem({
  children,
  text,
  big = false,
  className
}) {
  return (
    <div className={classNames('text-center', className)}>
      {children}
      <div className={classNames('text-white', big ? 'text-1xl sm:text-3xl' : 'text-xs sm:text-sm')}>{text}</div>
    </div>
  )
}

export function CoverLabelItem({
  label = '',
  textClass = 'text-white',
  children
}) {
  return (
    <>
      <div className='px-14 h-24 bg-secondary text-white rounded-sm inline-flex items-center text-sm'>{label}</div>
      <div className={classNames('mt-10 text-sm', textClass)}>{children}</div>
    </>
  )
}
